import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  supabase,
  handleSupabaseError,
  handleUnexpectedError,
  fetchAllRows,
  heldSecondsBetween,
} from '@/lib';
import type {
  Locker,
  LockerAssignment,
  LockerUsageStats,
  AssignLockerKeyData,
  ReturnLockerKeyData,
} from '@/types';
import { logActivity } from '@/utils';
import { LOCKER_QUERY_KEYS } from '@/constants';
import { invalidateLockerFamily } from '@/lib/query-invalidation';

const ASSIGNMENT_SELECT = `
  *,
  member:members ( id, first_name, last_name, email ),
  locker:lockers ( id, room, label, sort_order )
`;

/**
 * Active lockers by default; history can include retired lockers. Static
 * config seeded by migration (124 rows), hence the long staleTime.
 */
export function useLockers(includeInactive = false) {
  return useQuery({
    queryKey: includeInactive ? [...LOCKER_QUERY_KEYS.ALL, 'including-inactive'] : LOCKER_QUERY_KEYS.ALL,
    queryFn: async () => {
      let query = supabase
        .from('lockers')
        .select('*')
        .order('room', { ascending: true })
        .order('sort_order', { ascending: true });
      if (!includeInactive) query = query.eq('is_active', true);
      const { data, error } = await query;

      if (error) handleSupabaseError(error, { operation: 'fetching lockers' });
      return data as Locker[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Every key currently out (returned_at IS NULL), both rooms, newest first.
 * Bounded at one row per locker (≤124) by the DB's partial unique index. The
 * assign-modal "member already holds a key" warning derives from this list —
 * no separate query.
 */
export function useActiveLockerAssignments() {
  return useQuery({
    queryKey: LOCKER_QUERY_KEYS.ACTIVE_ASSIGNMENTS,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('locker_assignments')
        .select(ASSIGNMENT_SELECT)
        .is('returned_at', null)
        .order('assigned_at', { ascending: false });

      if (error) handleSupabaseError(error, { operation: 'fetching active locker assignments' });
      return data as LockerAssignment[];
    },
    refetchOnMount: 'always',
  });
}

/**
 * Paginated assignment history (server-side paging — history grows unbounded,
 * so we never ship all rows). Room filter rides on the inner locker join.
 */
export type LockerHistorySort = 'latest' | 'number';

export function useLockerAssignmentHistory(
  page: number, room: 'male' | 'female' | 'all', pageSize = 25,
  sort: LockerHistorySort = 'latest', label = '', lockerId?: string,
) {
  return useQuery({
    queryKey: lockerId
      ? [...LOCKER_QUERY_KEYS.HISTORY(page, room, pageSize, sort, label), lockerId]
      : LOCKER_QUERY_KEYS.HISTORY(page, room, pageSize, sort, label),
    queryFn: async () => {
      let query = supabase
        .from('locker_assignments')
        .select(`
          *,
          member:members ( id, first_name, last_name, email ),
          locker:lockers!inner ( id, room, label, sort_order )
        `, { count: 'exact' });

      // Top-level ordering through the to-one join, not locker.order (which
      // only orders embedded rows). Physical order handles 3B and 12B.
      if (sort === 'number') {
        query = query.order('locker(sort_order)', { ascending: true })
          .order('locker(room)', { ascending: true });
      }
      query = query.order('assigned_at', { ascending: false })
        .order('id', { ascending: false });
      if (lockerId) query = query.eq('locker_id', lockerId);
      if (label) query = query.eq('locker.label', label);

      if (room !== 'all') {
        query = query.eq('locker.room', room);
      }

      const { data, error, count } = await query.range(page * pageSize, (page + 1) * pageSize - 1);

      if (error) handleSupabaseError(error, { operation: 'fetching locker assignment history' });
      return { rows: data as LockerAssignment[], count: count ?? 0 };
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * Per-locker usage stats (get_locker_usage_stats RPC, one row per locker),
 * keyed by locker_id for O(1) grid lookups.
 */
export function useLockerUsageStats() {
  return useQuery({
    queryKey: LOCKER_QUERY_KEYS.USAGE_STATS,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_locker_usage_stats');

      if (error) handleSupabaseError(error, { operation: 'fetching locker usage stats' });
      return data as LockerUsageStats[];
    },
    select: (rows) =>
      Object.fromEntries(rows.map((row) => [row.locker_id, row])) as Record<string, LockerUsageStats>,
  });
}

/**
 * Every assignment (open + returned) for one member, newest first. Used by the
 * member profile "Lockers" tab. A frequent member's history grows without
 * bound, so this pages through PostgREST's 1000-row cap via fetchAllRows.
 */
export function useMemberLockerAssignments(memberId: string) {
  return useQuery({
    queryKey: LOCKER_QUERY_KEYS.BY_MEMBER(memberId),
    queryFn: async () => {
      const { data, error } = await fetchAllRows<LockerAssignment>(() =>
        supabase
          .from('locker_assignments')
          .select(`
            *,
            locker:lockers ( id, room, label, sort_order )
          `)
          .eq('member_id', memberId)
          .order('assigned_at', { ascending: false }),
      );

      if (error) handleSupabaseError(error, { operation: 'fetching member locker assignments', params: { memberId } });
      return data;
    },
    enabled: !!memberId,
    // Tab clicks remount this hook; refetch each visit so admins see fresh data.
    refetchOnMount: 'always',
  });
}

/**
 * Resolve the acting admin (for the denormalized *_by_name/email audit
 * columns), surfacing the underlying query error rather than masking it.
 */
async function getActingAdmin() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user?.id) {
    throw new Error('You must be logged in to perform this action');
  }

  const { data: adminDetails, error } = await supabase
    .from('admin_users')
    .select('full_name, email')
    .eq('id', session.user.id)
    .single();
  if (error || !adminDetails) {
    throw new Error(`Admin user details not found${error ? `: ${error.message}` : ''}`);
  }
  return adminDetails;
}

export function useAssignLockerKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AssignLockerKeyData) => {
      try {
        if (!data.locker_id || !data.member_id) {
          throw new Error('Missing required fields');
        }

        // Denormalize the acting admin onto the assignment (audit trail). No
        // member pre-fetch: the insert's select already returns the joined
        // member, and a bad member_id surfaces as a 23503 below.
        const adminDetails = await getActingAdmin();

        const { data: newAssignment, error } = await supabase
          .from('locker_assignments')
          .insert([{
            locker_id: data.locker_id,
            member_id: data.member_id,
            notes: data.notes?.trim() || null,
            assigned_by_name: adminDetails.full_name || adminDetails.email,
            assigned_by_email: adminDetails.email,
          }])
          .select(ASSIGNMENT_SELECT)
          .single();

        if (error) {
          // The one-open-per-locker partial unique index raises 23505.
          if (error.code === '23505') {
            throw new Error('This locker already has a key out. Refresh and pick another locker.');
          }
          if (error.code === '23503') {
            throw new Error('Member or locker no longer exists');
          }
          handleSupabaseError(error, { operation: 'assigning locker key' });
        }

        return { assignment: newAssignment as LockerAssignment };
      } catch (err) {
        handleUnexpectedError(err, { hookName: 'useAssignLockerKey', operation: 'assigning locker key' });
      }
    },
    onSuccess: (result, variables) => {
      void invalidateLockerFamily(queryClient, variables.member_id);

      if (result?.assignment) {
        logActivity(
          'locker_key_issued',
          'locker_assignment',
          result.assignment.id,
          {}, // creation — no field diff
          {
            locker_id: variables.locker_id,
            locker_label: result.assignment.locker?.label ?? null,
            locker_room: result.assignment.locker?.room ?? null,
            member_id: variables.member_id,
            member_name: result.assignment.member
              ? `${result.assignment.member.first_name} ${result.assignment.member.last_name}`
              : 'Unknown Member',
            member_email: result.assignment.member?.email ?? null,
            assigned_by_name: result.assignment.assigned_by_name,
            assigned_by_email: result.assignment.assigned_by_email,
            assignment_method: 'admin',
          },
          variables.member_id,
        );
      }
    },
  });
}

export function useReturnLockerKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ReturnLockerKeyData) => {
      try {
        if (!data.assignment_id) throw new Error('Missing assignment id');

        const adminDetails = await getActingAdmin();

        const { data: returned, error } = await supabase
          .from('locker_assignments')
          .update({
            returned_at: new Date().toISOString(),
            returned_by_name: adminDetails.full_name || adminDetails.email,
            returned_by_email: adminDetails.email,
          })
          .eq('id', data.assignment_id)
          .is('returned_at', null) // concurrency guard: a double-return matches 0 rows
          .select(ASSIGNMENT_SELECT)
          .single();

        if (error) {
          if (error.code === 'PGRST116') {
            throw new Error('This key was already returned.');
          }
          handleSupabaseError(error, { operation: 'returning locker key', params: { id: data.assignment_id } });
        }

        return { assignment: returned as LockerAssignment };
      } catch (err) {
        handleUnexpectedError(err, { hookName: 'useReturnLockerKey', operation: 'returning locker key' });
      }
    },
    onSuccess: (result, variables) => {
      void invalidateLockerFamily(queryClient, variables.member_id);

      const assignment = result?.assignment;
      if (assignment) {
        const heldSeconds = assignment.returned_at
          ? heldSecondsBetween(assignment.assigned_at, assignment.returned_at)
          : null;

        logActivity(
          'locker_key_returned',
          'locker_assignment',
          assignment.id,
          {}, // state transition — metadata carries the details
          {
            locker_id: variables.locker_id,
            locker_label: assignment.locker?.label ?? null,
            locker_room: assignment.locker?.room ?? null,
            member_id: variables.member_id,
            member_name: assignment.member
              ? `${assignment.member.first_name} ${assignment.member.last_name}`
              : 'Unknown Member',
            member_email: assignment.member?.email ?? null,
            assigned_at: assignment.assigned_at,
            returned_at: assignment.returned_at,
            held_seconds: heldSeconds,
            returned_by_name: assignment.returned_by_name,
            returned_by_email: assignment.returned_by_email,
          },
          variables.member_id,
        );
      }
    },
  });
}
